/**
 * Error telemetry.
 *
 * Reports frontend errors to the backend so the flock portal can show them
 * alongside the backend's own. This is silent and automatic, and is a
 * different thing from the user-facing "Report" modal gated behind
 * REACT_APP_ENABLE_ERROR_REPORT_V2.
 *
 * Three rules this file exists to enforce:
 *
 *  1. Never throw. A telemetry failure must not become a second error.
 *  2. Never use httpService. Reporting through the axios instance would route
 *     a failed report back through the interceptor that generates reports.
 *  3. Never flood. A render loop can produce thousands of identical errors a
 *     second; per-fingerprint suppression and a session cap keep that from
 *     becoming a denial of service against our own backend.
 */

import * as constants from "Constants";

const ENABLED = process.env.REACT_APP_ENABLE_ERROR_TELEMETRY === "true";

// The flock API is mounted at Django's root, and nginx rewrites /api/(.*) to
// /$1 in production. So "<base_url>/flock/..." resolves correctly both in
// production (base_url ends in /api, which nginx strips) and in development
// (base_url points straight at Django).
const ENDPOINT = `${constants.base_url}/flock/errors/ingest/`;

/** Do not send the same error more than once per this many ms. */
const DEDUP_WINDOW_MS = 60000;
/** Hard stop for one page session, so a loop cannot keep reporting forever. */
const SESSION_LIMIT = 20;
/** Wait this long to batch errors that arrive together. */
const FLUSH_DELAY_MS = 2000;
const MAX_BATCH = 5;

const MAX_MESSAGE = 2000;
const MAX_STACK = 20000;

const lastSent = new Map();
let sessionCount = 0;
let queue = [];
let flushTimer = null;
let paused = false;

function fingerprint(message, stack) {
  const head = (stack || "").split("\n").slice(0, 4).join("|");
  return `${message || ""}|${head}`.slice(0, 500);
}

function suppressed(key) {
  const now = Date.now();
  const previous = lastSent.get(key);
  if (previous && now - previous < DEDUP_WINDOW_MS) return true;
  lastSent.set(key, now);
  return false;
}

function send(events, useBeacon = false) {
  const body = JSON.stringify({ events });
  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
      return;
    }
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "include",
    }).catch(() => {});
  } catch (e) {
    /* telemetry must never throw */
  }
}

function flush(useBeacon = false) {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!queue.length) return;
  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(MAX_BATCH);
  send(batch, useBeacon);
  if (queue.length) scheduleFlush();
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => flush(false), FLUSH_DELAY_MS);
}

/**
 * Report one error. Safe to call from anywhere, including an error handler.
 */
export function reportError({
  message,
  stack,
  source = "nodejs",
  level = "ERROR",
  excType = "",
  context = {},
} = {}) {
  try {
    if (!ENABLED || paused) return;
    if (sessionCount >= SESSION_LIMIT) return;

    const text = String(message || "Unknown error").slice(0, MAX_MESSAGE);
    const trace = String(stack || "").slice(0, MAX_STACK);
    if (suppressed(fingerprint(text, trace))) return;

    sessionCount += 1;
    queue.push({
      source,
      level,
      message: text,
      exc_type: excType || "",
      stack: trace,
      url: typeof window !== "undefined" ? window.location.href : "",
      occurred_at: new Date().toISOString(),
      context: {
        ...context,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        release: process.env.REACT_APP_VERSION || "",
      },
    });
    scheduleFlush();
  } catch (e) {
    /* never throw */
  }
}

/**
 * Stop reporting while the site is closed for maintenance. Every request
 * fails then, by design, and reporting each one would flood the flock portal
 * with an outage it already knows about.
 */
export function setTelemetryPaused(value) {
  paused = Boolean(value);
}

/**
 * Catch errors that escape React: uncaught exceptions and rejected promises.
 * Call once, before the app renders, so boot-time failures are covered too.
 */
export function installGlobalErrorHandlers() {
  if (!ENABLED || typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    reportError({
      message: event?.message,
      stack: event?.error?.stack,
      excType: event?.error?.name || "Error",
      context: { kind: "window.onerror", filename: event?.filename },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    reportError({
      message: reason?.message || String(reason),
      stack: reason?.stack,
      excType: reason?.name || "UnhandledRejection",
      context: { kind: "unhandledrejection" },
    });
  });

  // Anything still queued when the tab goes away.
  window.addEventListener("pagehide", () => flush(true));
}

export default { reportError, installGlobalErrorHandlers, setTelemetryPaused };
